import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal, effect } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { BreadcrumbService } from '@card-software/library';
import { FabricProxyService } from '../../../proxies/fabric.proxy.service';
import { MonthlyCostProxyService, RollMonthlyCostDto, RollSearchCommand } from '../../../proxies/monthly-cost.proxy.service';
import { CostReportsCacheService } from '../services/cost-reports-cache.service';
import {
  ALL_COST_COMPONENTS,
  COST_CENTER_TO_PROCESS,
  COST_COMPONENT_LABELS,
  ColorDisplay,
  CostComponentKey,
  CostSource,
  HierarchyLevel,
  ProcessDetailContext,
  ProcessKey,
  PROCESS_LABELS,
  resolveColorDisplay,
  resolveFabricDescription,
} from '../models/cost-analysis.models';
import { isProductionProcess, PROCESS_COLORS, PROCESS_FLOW, ProcessColorTokens, ProductionProcessKey } from '../models/process-colors';
import { CostAnalysisStateService } from '../services/cost-analysis-state.service';

interface ComponentBreakdownRow {
  key: CostComponentKey;
  label: string;
  realCostUsd: number;
  realCostPerLb: number;
  pctOfProcess: number;
  /** True when the component has no measurable cost — rendered as muted and sorted to the bottom. */
  isEmpty: boolean;
  /** True when this row is the dominant cost component for the process. */
  isDominant: boolean;
}

interface DistributionRow {
  label: string;
  level: string;
  weightLbs: number;
  weightPct: number;
  realCostUsd: number;
  realCostPerLb: number;
  costSourceTag: 'real' | 'standard' | 'mixed' | null;
  /** Populated only when the row represents a roll. */
  colorReference?: string | null;
}

/**
 * Process Detail page — shows cost-component breakdown for a specific
 * process at a given hierarchy level (Fabric / WO / PC / KO / Roll).
 *
 * Navigated to from By Fabric or By Process when a process cell is clicked.
 * Reads the already-loaded rolls from CostAnalysisStateService (populated by
 * cost-analysis-page) and filters them down to the selected scope — no extra
 * API calls unless the state is empty, in which case it refetches as a safety
 * net.
 *
 * Phase 1: Real cost only. Standard/variance columns show "—".
 */
@Component({
  selector: 'app-process-detail-page',
  templateUrl: './process-detail-page.component.html',
  styleUrl: './process-detail-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessDetailPageComponent implements OnInit {
  private readonly monthlyCostProxy = inject(MonthlyCostProxyService);
  private readonly fabricProxy = inject(FabricProxyService);
  private readonly router = inject(Router);
  private readonly state = inject(CostAnalysisStateService);
  private readonly cache = inject(CostReportsCacheService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  private readonly grandTotalOverride = signal<number | null>(null);

  readonly context = signal<ProcessDetailContext | null>(null);
  readonly loading = signal(false);
  readonly hasLoaded = signal(false);
  readonly componentBreakdown = signal<ComponentBreakdownRow[]>([]);
  readonly distribution = signal<DistributionRow[]>([]);
  readonly processTotalUsd = signal(0);
  readonly processTotalLbs = signal(0);
  readonly processCostPerLb = signal(0);
  readonly fabricDescription = signal<string | null>(null);

  /** When false, empty (zero-cost) component rows collapse into a single
   *  "{N} components inactive · show all" affordance. Default-collapsed
   *  per the brief — hidden rows surface only on user request. */
  readonly showAllEmpty = signal(false);

  readonly processLabels = PROCESS_LABELS;
  readonly processFlow: readonly ProductionProcessKey[] = PROCESS_FLOW;

  /**
   * Localized month abbreviations for the period chip in the header. Indexed
   * 0-11; the template reads `monthAbbr[ctx.month - 1]`.
   */
  private readonly monthAbbr: readonly string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** Share of the period's grand total cost consumed by this process. */
  readonly pctOfGrandTotal = computed(() => {
    const grand = this.grandTotalOverride() ?? 0;
    if (grand <= 0) {
      return 0;
    }
    return (this.processTotalUsd() / grand) * 100;
  });

  /** True when the selected scope returned no data to show. */
  readonly isEmpty = computed(() => this.hasLoaded() && !this.loading() && this.processTotalUsd() === 0 && this.processTotalLbs() === 0);

  /**
   * The brand color tokens for the currently-selected process. Drives the
   * process badge, dot, stage indicator's active segment, primary % bar
   * fill, and the primary-metric accent line. Returns `null` only when the
   * context's process is `RawMaterial`, which can't actually reach this
   * page today (the by-fabric Raw Material cell isn't a clickable
   * drill-down) — but the guard keeps the type system honest.
   */
  readonly currentColors = computed<ProcessColorTokens | null>(() => {
    const ctx = this.context();
    if (!ctx || !isProductionProcess(ctx.process)) {
      return null;
    }
    return PROCESS_COLORS[ctx.process];
  });

  /**
   * Zero-based position of the current process in the production flow
   * (Knitting=0 → Rolling=5). Drives the stage indicator: the segment at
   * this index renders at full opacity, the others at 30%.
   */
  readonly stageIndex = computed(() => {
    const ctx = this.context();
    if (!ctx || !isProductionProcess(ctx.process)) {
      return -1;
    }
    return PROCESS_FLOW.indexOf(ctx.process);
  });

  /** Component rows actually visible in the table — empties only when the
   *  user opted in via "show all". The default state hides the noise. */
  readonly visibleBreakdown = computed(() => {
    const all = this.componentBreakdown();
    return this.showAllEmpty() ? all : all.filter((r) => !r.isEmpty);
  });

  /** Number of empty components currently hidden behind the "show all"
   *  toggle. 0 ⇒ no collapsed-row affordance is rendered. */
  readonly hiddenEmptyCount = computed(() => this.componentBreakdown().filter((r) => r.isEmpty).length);

  /**
   * Localized period chip ("Feb 2026"). Empty string before context lands.
   */
  readonly periodLabel = computed(() => {
    const ctx = this.context();
    if (!ctx) return '';
    const m = this.monthAbbr[ctx.month - 1] ?? '';
    return `${m} ${ctx.year}`;
  });

  /**
   * Trailing identifier for the breadcrumb's third segment. Picks the most
   * specific scope present in context (roll → PC/KO → WO → fabric).
   * Returns `null` to drop the segment entirely when no scope applies.
   */
  readonly breadcrumbScope = computed<string | null>(() => {
    const ctx = this.context();
    if (!ctx) return null;
    return (
      ctx.context.rollNumber ??
      ctx.context.productionCardNumber ??
      ctx.context.knittingOrderNumber ??
      ctx.context.workOrderNumber ??
      ctx.context.fabricCode ??
      null
    );
  });

  readonly breadcrumbLabel = computed(() => {
    const ctx = this.context();
    const processLabel = ctx ? this.processLabels[ctx.process] : '';
    const scopeLabel = this.breadcrumbScope();
    return ['Process Detail', processLabel, scopeLabel].filter(Boolean).join(' - ');
  });

  private readonly syncBreadcrumb = effect(() => this.breadcrumbService.setCurrent(this.breadcrumbLabel()), { allowSignalWrites: true });

  toggleShowAllEmpty(): void {
    this.showAllEmpty.update((v) => !v);
  }

  /** Color used to paint a single stage-indicator segment. Each segment
   *  carries its own process color (so the row reads as the production
   *  flow's color signature, not a single brand color). */
  processColorFor(process: ProductionProcessKey): string {
    return PROCESS_COLORS[process].light;
  }

  /**
   * Resolve a distribution row's color display via the page-load color map.
   * Returns `undefined` for higher-level rows (WO, PC/KO) that aggregate
   * multiple rolls and therefore have no single color to render.
   */
  rowColor(row: DistributionRow): ColorDisplay | undefined {
    if (row.colorReference === undefined) {
      return undefined;
    }
    return resolveColorDisplay(row.colorReference, this.state.colorMap());
  }

  ngOnInit(): void {
    const nav = this.router.getCurrentNavigation();
    const state = (nav?.extras?.state ?? history.state) as { detailContext?: ProcessDetailContext } | undefined;
    if (state?.detailContext) {
      this.context.set(state.detailContext);
      this.loadDetail(state.detailContext);
    }
  }

  back(): void {
    this.router.navigate(['/cost-analysis']);
  }

  private loadDetail(ctx: ProcessDetailContext): void {
    const processCostCenters = Object.entries(COST_CENTER_TO_PROCESS)
      .filter(([, p]) => p === ctx.process)
      .map(([cc]) => cc);

    if (ctx.context.fabricDescription) {
      this.fabricDescription.set(ctx.context.fabricDescription);
    } else if (ctx.context.fabricCode) {
      this.fabricProxy
        .getByCode(ctx.context.fabricCode)
        .pipe(catchError(() => of({ success: false, data: null, errorMessage: null })))
        .subscribe((resp) => {
          if (resp.success && resp.data) {
            this.fabricDescription.set(resolveFabricDescription(resp.data) ?? null);
          }
        });
    }

    this.cache
      .getProcessSummary(ctx.year, ctx.month, this.state.costSourceFilter())
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe((resp) => {
        if (resp?.success && resp.data) {
          this.grandTotalOverride.set(resp.data.matrixTotals.totalCostUsd);
        }
      });

    this.loading.set(true);
    const cmd: RollSearchCommand = {
      year: ctx.year,
      month: ctx.month,
      costSource: this.state.costSourceFilter(),
      costCenterCodes: processCostCenters,
      rollNumbers: ctx.context.rollNumber ? [ctx.context.rollNumber] : null,
      productionCardNumbers: this.pickPcOrKoFilter(ctx),
      workOrderReferences: ctx.context.workOrderReference ? [ctx.context.workOrderReference] : null,
      fabricCodes: ctx.context.fabricCode ? [ctx.context.fabricCode] : null,
    };
    this.monthlyCostProxy
      .searchRolls(cmd, 1, 1000)
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.loading.set(false);
          this.hasLoaded.set(true);
        }),
      )
      .subscribe((resp) => {
        const rolls = resp && resp.success && resp.data ? resp.data.items : [];
        this.computeFromRolls(rolls, ctx, processCostCenters);
      });
  }

  private pickPcOrKoFilter(ctx: ProcessDetailContext): string[] | null {
    if (ctx.context.productionCardNumber) return [ctx.context.productionCardNumber];
    if (ctx.context.knittingOrderNumber) return [ctx.context.knittingOrderNumber];
    return null;
  }

  private computeFromRolls(rolls: RollMonthlyCostDto[], ctx: ProcessDetailContext, processCostCenters: string[]): void {
    this.buildBreakdown(rolls, ctx.process, processCostCenters);
    this.buildDistribution(rolls, ctx, processCostCenters);
  }

  /**
   * Aggregate costBreakdownUsd from areaCosts matching the selected process.
   */
  private buildBreakdown(rolls: RollMonthlyCostDto[], process: ProcessKey, costCenters: string[]): void {
    const breakdown: Record<string, number> = {};
    let totalProcessCost = 0;
    let totalProcessLbs = 0;

    for (const roll of rolls) {
      for (const entry of roll.areaCosts ?? []) {
        if (!costCenters.includes(entry.costCenterCode)) continue;

        totalProcessCost += entry.totalCostUsd ?? 0;
        totalProcessLbs += entry.weightLbs ?? 0;

        for (const [key, value] of Object.entries(entry.costBreakdownUsd ?? {})) {
          breakdown[key] = (breakdown[key] ?? 0) + value;
        }
      }
    }

    this.processTotalUsd.set(totalProcessCost);
    this.processTotalLbs.set(totalProcessLbs);
    this.processCostPerLb.set(totalProcessLbs > 0 ? totalProcessCost / totalProcessLbs : 0);

    // Start from the canonical component order and append any unknown keys so the
    // UI always renders a stable layout regardless of what the backend emits.
    const seen = new Set<string>();
    const rows: ComponentBreakdownRow[] = [];

    for (const key of ALL_COST_COMPONENTS) {
      const value = breakdown[key] ?? 0;
      seen.add(key);
      rows.push({
        key,
        label: COST_COMPONENT_LABELS[key] ?? key,
        realCostUsd: value,
        realCostPerLb: totalProcessLbs > 0 ? value / totalProcessLbs : 0,
        pctOfProcess: totalProcessCost > 0 ? (value / totalProcessCost) * 100 : 0,
        isEmpty: value <= 0,
        isDominant: false,
      });
    }
    for (const [key, value] of Object.entries(breakdown)) {
      if (seen.has(key)) continue;
      rows.push({
        key: key as CostComponentKey,
        label: COST_COMPONENT_LABELS[key] ?? key,
        realCostUsd: value,
        realCostPerLb: totalProcessLbs > 0 ? value / totalProcessLbs : 0,
        pctOfProcess: totalProcessCost > 0 ? (value / totalProcessCost) * 100 : 0,
        isEmpty: value <= 0,
        isDominant: false,
      });
    }

    // Tag the dominant component (highest % of process) so the template can
    // surface it with a primary-color left border.
    let maxPct = 0;
    let dominantKey: CostComponentKey | null = null;
    for (const row of rows) {
      if (!row.isEmpty && row.pctOfProcess > maxPct) {
        maxPct = row.pctOfProcess;
        dominantKey = row.key;
      }
    }
    if (dominantKey) {
      rows.find((r) => r.key === dominantKey)!.isDominant = true;
    }

    // Sort: non-empty rows by descending cost, then empty rows at the bottom
    // so the breakdown reads top-to-bottom from most impactful to "pending".
    rows.sort((a, b) => {
      if (a.isEmpty !== b.isEmpty) return a.isEmpty ? 1 : -1;
      return b.realCostUsd - a.realCostUsd;
    });

    this.componentBreakdown.set(rows);
  }

  /**
   * Build distribution rows showing how cost is spread across children.
   * Fabric → by WO, WO → by PC/KO, PC/KO → by Roll, Roll → self.
   */
  private buildDistribution(rolls: RollMonthlyCostDto[], ctx: ProcessDetailContext, costCenters: string[]): void {
    const childLevel = this.getChildLevel(ctx.level);
    if (!childLevel) {
      this.distribution.set([]);
      return;
    }

    const groups = new Map<string, { label: string; rolls: RollMonthlyCostDto[] }>();
    for (const roll of rolls) {
      const key = this.getChildGroupKey(roll, ctx.level);
      let group = groups.get(key.key);
      if (!group) {
        group = { label: key.label, rolls: [] };
        groups.set(key.key, group);
      }
      group.rolls.push(roll);
    }

    let totalLbs = 0;
    const distRows: DistributionRow[] = [];

    for (const [, group] of groups) {
      let costUsd = 0;
      let lbs = 0;
      let hasReal = false;
      let hasStandard = false;
      for (const roll of group.rolls) {
        for (const entry of roll.areaCosts ?? []) {
          if (!costCenters.includes(entry.costCenterCode)) continue;
          costUsd += entry.totalCostUsd ?? 0;
          lbs += entry.weightLbs ?? 0;
          if (entry.costSource === CostSource.Standard) hasStandard = true;
          else hasReal = true;
        }
      }
      totalLbs += lbs;
      const colorReference = childLevel === 'Roll' && group.rolls.length === 1 ? (group.rolls[0].colorReference ?? null) : undefined;
      const costSourceTag: DistributionRow['costSourceTag'] =
        hasReal && hasStandard ? 'mixed' : hasStandard ? 'standard' : hasReal ? 'real' : null;
      distRows.push({
        label: group.label,
        level: childLevel,
        weightLbs: lbs,
        weightPct: 0,
        realCostUsd: costUsd,
        realCostPerLb: lbs > 0 ? costUsd / lbs : 0,
        costSourceTag,
        colorReference,
      });
    }

    for (const row of distRows) {
      row.weightPct = totalLbs > 0 ? (row.weightLbs / totalLbs) * 100 : 0;
    }

    distRows.sort((a, b) => b.realCostUsd - a.realCostUsd);
    this.distribution.set(distRows);
  }

  private getChildLevel(level: HierarchyLevel): string | null {
    switch (level) {
      case 'fabric':
        return 'Color';
      case 'color':
        return 'Work Order';
      case 'workOrder':
        return 'PC / KO';
      case 'productionCard':
      case 'knittingOrder':
        return 'Roll';
      case 'roll':
        return null;
    }
  }

  private getChildGroupKey(roll: RollMonthlyCostDto, parentLevel: HierarchyLevel): { key: string; label: string } {
    switch (parentLevel) {
      case 'fabric': {
        // Group rolls by their colorReference. The label is resolved via the
        // shared color map when available; falls back to the raw reference
        // (or the "(Uncolored)" placeholder) so the row never renders blank.
        const colorRef = roll.colorReference ?? null;
        if (colorRef === null) {
          return { key: '__uncolored__', label: '(Uncolored / Pending)' };
        }
        const resolved = this.state.colorMap().get(colorRef);
        return { key: colorRef, label: resolved?.name ?? colorRef };
      }
      case 'color':
        return {
          key: roll.workOrderKey?.reference ?? 'no-wo',
          label: roll.workOrderKey?.number ?? '(No Work Order)',
        };
      case 'workOrder':
        if (roll.productionCardKey?.reference) {
          return { key: roll.productionCardKey.reference, label: roll.productionCardKey.number };
        }
        if (roll.knittingOrderKey?.reference) {
          return { key: roll.knittingOrderKey.reference, label: roll.knittingOrderKey.number };
        }
        return { key: 'no-pc-ko', label: '(No PC/KO)' };
      case 'productionCard':
      case 'knittingOrder':
        return { key: roll.number, label: roll.number };
      default:
        return { key: roll.number, label: roll.number };
    }
  }
}
